import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  RefreshControl,
  Pressable,
  Platform,
} from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { RootStackParamList, ClubStats } from '../types';
import { AppHeader, ScreenLayout, ClubMark, ClubLogo, EmptyState, PageTitle } from '../components';
import { useResponsive } from '../utils/responsive';
import { Colors, Fonts, Radii, Shadows, Spacing, Typography } from '../constants';
import { clubsService } from '../services/clubs';
import { useDialog } from '../contexts';

type Props = NativeStackScreenProps<RootStackParamList, 'ClubLeaderboard'>;

// Gold / silver / bronze medal fills for the top three
const MEDAL = ['#D4A017', '#9AA3A8', '#B8743A'];

/**
 * ClubLeaderboardCard - Sub-component for displaying club ranking
 */
interface ClubLeaderboardCardProps {
  clubStats: ClubStats;
  rank: number;
  mobile: boolean;
  onPress: () => void;
}

const ClubLeaderboardCard: React.FC<ClubLeaderboardCardProps> = ({
  clubStats,
  rank,
  mobile,
  onPress,
}) => {
  const { t } = useTranslation();
  const top = rank <= 3;
  const name = clubStats.club?.name || 'Unknown Club';
  const meta = [clubStats.club?.location, !mobile ? clubStats.club?.description : null].filter(Boolean).join(' — ');

  return (
    <Pressable
      style={({ hovered, pressed }: any) => [
        styles.card,
        mobile && styles.cardMobile,
        hovered && Shadows.cardHover,
        pressed && styles.cardPressed,
      ]}
      onPress={onPress}
    >
      <View style={[styles.rank, top && { backgroundColor: MEDAL[rank - 1] }]}>
        {top ? (
          <Ionicons name="trophy" size={18} color={Colors.white} />
        ) : (
          <Text style={styles.rankText}>{rank}</Text>
        )}
      </View>

      {clubStats.club?.logo_url ? (
        <ClubLogo logoUrl={clubStats.club.logo_url} size={mobile ? 36 : 40} />
      ) : (
        <ClubMark name={name} size={mobile ? 36 : 40} />
      )}

      <View style={styles.clubInfo}>
        <Text style={styles.clubName} numberOfLines={1}>{name}</Text>
        {meta ? <Text style={styles.clubMeta} numberOfLines={1}>{meta}</Text> : null}
      </View>

      <View style={[styles.stats, mobile && styles.statsMobile]}>
        {[
          ['people-outline', clubStats.member_count, t('screens.clubLeaderboard.members')],
          ['pricetag-outline', clubStats.active_listings_count, t('screens.clubLeaderboard.listings')],
        ].map(([icon, n, label]) => (
          <View key={String(label)} style={styles.stat}>
            <View style={styles.statValueRow}>
              <Ionicons name={icon as any} size={14} color={Colors.accentStrong} />
              <Text style={styles.statValue}>{n}</Text>
            </View>
            {!mobile && <Text style={[Typography.overline, styles.statLabel]}>{label}</Text>}
          </View>
        ))}
      </View>
    </Pressable>
  );
};

export const ClubLeaderboardScreen: React.FC<Props> = ({ navigation }) => {
  const { t } = useTranslation();
  const dialog = useDialog();
  const dialogRef = useRef(dialog);
  dialogRef.current = dialog;
  const [leaderboard, setLeaderboard] = useState<ClubStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const { isDesktop } = useResponsive();

  const loadLeaderboard = useCallback(async () => {
    try {
      const data = await clubsService.getClubLeaderboard(20); // Top 20 clubs
      setLeaderboard(data);
    } catch (error: any) {
      console.error('[ClubLeaderboardScreen] Failed to load leaderboard:', error);
      dialogRef.current.alert(
        t('common.errors.title'),
        t('screens.clubLeaderboard.loadError'),
        'error'
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [t]);

  useEffect(() => {
    loadLeaderboard();
  }, [loadLeaderboard]);

  const onRefresh = useCallback(() => {
    setRefreshing(true);
    loadLeaderboard();
  }, [loadLeaderboard]);

  const handleClubPress = (clubId: string) => {
    navigation.navigate('ClubDetail', { clubId });
  };

  return (
    <ScreenLayout maxWidth={800} desktopPadding>
      <View style={styles.container}>
        {/* Mobile header */}
        {!isDesktop && (
          <AppHeader
            navigation={navigation}
            currentScreen="ClubLeaderboard"
            title={t('screens.clubLeaderboard.title')}
            showBackButton={true}
          />
        )}

        {loading ? (
          <View style={styles.centerContainer}>
            <Text>{t('common.loading')}</Text>
          </View>
        ) : (
          <FlatList
            data={leaderboard}
            keyExtractor={(item) => item.club_id}
            renderItem={({ item, index }) => (
              <ClubLeaderboardCard
                clubStats={item}
                rank={index + 1}
                mobile={!isDesktop}
                onPress={() => handleClubPress(item.club_id)}
              />
            )}
            ListHeaderComponent={
              isDesktop ? (
                <PageTitle
                  title={t('screens.clubLeaderboard.title')}
                  subtitle={t('screens.clubLeaderboard.subtitle')}
                  onBack={() => navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Clubs')}
                />
              ) : (
                <View style={styles.listHeader}>
                  <Text style={styles.subtitle}>{t('screens.clubLeaderboard.subtitle')}</Text>
                </View>
              )
            }
            contentContainerStyle={[
              styles.listContent,
              isDesktop && styles.listContentDesktop,
              leaderboard.length === 0 && styles.listContentEmpty,
            ]}
            refreshControl={
              <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
            }
            ListEmptyComponent={
              <EmptyState
                variant="full"
                icon="trophy-outline"
                title={t('screens.clubLeaderboard.noClubs')}
                subtitle={t('screens.clubLeaderboard.noClubsSubtext')}
              />
            }
            initialNumToRender={10}
            maxToRenderPerBatch={10}
            windowSize={5}
            removeClippedSubviews={true}
          />
        )}
      </View>
    </ScreenLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.white,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  listHeader: {
    marginBottom: 14,
  },
  subtitle: {
    fontFamily: Fonts.regular,
    fontSize: 14,
    lineHeight: 21,
    color: Colors.textMedium,
  },
  listContent: {
    padding: Spacing.base,
    paddingBottom: Spacing['3xl'],
    gap: 10,
  },
  listContentDesktop: {
    padding: 0,
  },
  listContentEmpty: {
    flex: 1,
  },
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.base,
    backgroundColor: Colors.white,
    borderRadius: Radii.card,
    borderWidth: 1,
    borderColor: Colors.borderLight,
    paddingVertical: 14,
    paddingHorizontal: 18,
    ...Shadows.card,
    ...(Platform.OS === 'web' && { cursor: 'pointer', touchAction: 'pan-y' } as any),
  },
  cardMobile: {
    gap: Spacing.md,
    paddingVertical: Spacing.md,
    paddingHorizontal: 14,
  },
  cardPressed: {
    opacity: 0.9,
  },
  rank: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: Colors.background,
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  rankText: {
    fontFamily: Fonts.bold,
    fontSize: 15,
    color: Colors.textMedium,
  },
  clubInfo: {
    flex: 1,
    minWidth: 0,
  },
  clubName: {
    fontFamily: Fonts.semiBold,
    fontSize: 15,
    lineHeight: 20,
    color: Colors.textDark,
  },
  clubMeta: {
    fontFamily: Fonts.regular,
    fontSize: 13,
    color: Colors.textMedium,
  },
  stats: {
    flexDirection: 'row',
    gap: 22,
    flexShrink: 0,
  },
  statsMobile: {
    gap: Spacing.md,
  },
  stat: {
    alignItems: 'center',
  },
  statValueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.xs,
  },
  statValue: {
    fontFamily: Fonts.bold,
    fontSize: 15,
    color: Colors.textDark,
  },
  statLabel: {
    color: Colors.textLight,
    marginTop: 5,
  },
});
